<script lang="ts" setup>
import { ref, watch } from 'vue'
import { $fetch } from '@@home/plugins/fetch';
import { useAsyncData } from '@@home/hooks/async-data';
import useMarkdown from '@@home/hooks/markdown';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import dayjsZh from 'dayjs/locale/zh-cn';
import dayjsEn from 'dayjs/locale/en';
import Combobox from '@@home/components/Combobox.vue';
import Listbox from '@@home/components/Listbox.vue';

const DEFAULT_YEAR = 2023;
// 老数据截止日期
const BREAK_DATE = '2024-02-10 00:00:00';

const { locale } = useI18n()

const currentDayjsLocale = locale.value === 'zh-CN' ? dayjsZh : dayjsEn;

const marked = useMarkdown();
const currentYear = new Date().getFullYear();
const searchParams = ref({ title: '', year: currentYear, region: [] });
const loading = ref(false);
let loadingTimer: ReturnType<typeof setTimeout>;

//获取活动列表
const { data: listing, refresh, pending } = useAsyncData(
  'events:listing',
  () => $fetch('/activity/v1/overview', {
    data: {
      title: searchParams.value.title,
      year: searchParams.value.year,
      country: searchParams.value.region?.[0] ?? '',
      province: searchParams.value.region?.[1] ?? '',
      city: searchParams.value.region?.[2] ?? '',
    },
  }),
);

const { data: regionOptions, refresh: refreshRegion } = useAsyncData<Record<string, any>[]>(
  'events:region',
  () => $fetch('/activity/v1/region/selector', {
    data: {
      title: searchParams.value.title,
      year: searchParams.value.year,
    },
  }),
  {
    server: false,
  },
);

//日期格式化
const formatDateRange = (startDate: string, endDate: string) => {
  const start = dayjs(startDate).locale(currentDayjsLocale);
  const end = dayjs(endDate).locale(currentDayjsLocale);

  if (start.year() !== end.year() || start.month() !== end.month()) {
    return locale.value === 'zh-CN' ? `${start.format('M月D日')} - ${end.format('M月D日')}` : `${start.format('MMM D')} - ${end.format('MMM D')}`;
  }

  if (start.date() !== end.date()) {
    return locale.value === 'zh-CN' ? `${start.format('M月D日')} - ${end.format('D日')}` : `${start.format('MMM D')} - ${end.format('D')}`;
  }

  if (start.format('HH:mm:ss') === '00:00:00' && end.format('HH:mm:ss') === '23:59:59') {
    return locale.value === 'zh-CN' ? `${start.format('M月D日')}` : `${start.format('MMM D')}`;
  }

  return locale.value === 'zh-CN' ? `${start.format('M月D日 HH:mm')} - ${end.format('HH:mm')}` : `${start.format('MMM D HH:mm')} - ${end.format('HH:mm')}`;
};

//查询时触发的函数
const search = async () => {
  refreshRegion();
  await refresh();

  if (listing.value.length === 0) {
    searchParams.value.region = [];
  }
};

//重置时触发
const resetForm = () => {
  searchParams.value.title = '';
  searchParams.value.year = currentYear;
  searchParams.value.region = [];
  search();
};

const yearOptions = Array.from({ length: currentYear - DEFAULT_YEAR + 1 }, (_, index) => ({
  value: DEFAULT_YEAR + index,
  label: DEFAULT_YEAR + index,
})).reverse();

watch(pending, (value) => {
  clearTimeout(loadingTimer);

  if (value === true) {
    loadingTimer = setTimeout(() => {
      loading.value = value;
    }, 500);
  }
  else {
    loading.value = value;
  }
});
</script>

<template>
  <main class="page-events pb-160px color-hex-2d2d2d">

    <section class="events-title h-400px bg-cover bg-center bg-no-repeat">
      <div class="m-0 mx-auto pt-143px text-50px font-medium leading-70px container">{{ $t('events.title') }}</div>
    </section>

    <div class="mx-auto container">
      <div class="mt-20px flex justify-between">
        <div class="h-40px w-364px flex text-16px leading-40px <2xl:w-312px">
          <div class="flex-shrink-0">{{ $t('events.actionTitle') }}</div>
          <input type="text" name="title" :placeholder="$t('events.placeholder1')" v-model="searchParams.title" @keyup.enter="search" class="gray-border ml-16px box-border h-40px flex-grow px-16px">
        </div>

        <div class="h-40px w-364px flex text-16px leading-40px <2xl:w-312px">
          <div class="flex-shrink-0">{{ $t('events.actionYear') }}</div>
          <Listbox v-model="searchParams.year" :options="yearOptions" class="ml-16px flex-grow" />
        </div>

        <div class="h-40px w-364px flex text-16px leading-40px <2xl:w-312px">
          <div class="flex-shrink-0">{{ $t('events.actionCity') }}</div>
          <Combobox
            v-model="searchParams.region"
            :options="regionOptions"
            class="ml-16px flex-grow"
            :placeholder="$t('events.placeholder2')"
            :not-found-text="$t('events.regionNotFound')"
            :empty-text="$t('events.regionEmpty')"
            value-key="code"
            label-key="name"
            path-mode
          >
            <template #label="{ data, active, selected }">
              <template v-if="data.$level === 0">
                <div
                  class="flex items-center justify-center whitespace-nowrap after:h-1px after:w-18px after:flex-shrink-0 after:content-empty before:h-1px before:w-18px before:flex-shrink-0 before:content-empty"
                  :class="{
                    'before:bg-brand-primary after:bg-brand-primary': active || selected,
                    'before:bg-hex-2d2d2d after:bg-hex-2d2d2d': !active && !selected,
                  }"
                >
                  <span class="inline-block px-8px">{{ data.name }}</span>
                </div>
              </template>
              <template v-else-if="data.$level === 2">
                <span
                  class="ml-4px flex items-center whitespace-nowrap before:h-2px before:w-2px before:flex-shrink-0 before:rounded-1\/2 before:content-empty"
                  :class="{
                    'before:bg-brand-primary': active || selected,
                    'before:bg-hex-2d2d2d': !active && !selected,
                  }"
                >
                  <span class="inline-block px-8px">{{ data.name }}</span>
                </span>
              </template>
              <template v-else>
                <span class="whitespace-nowrap pr-8px">{{ data.name }}</span>
              </template>
            </template>
          </Combobox>
        </div>

        <div class="flex">
          <button type="button" @click="search" class="gray-border h-40px flex cursor-pointer items-center justify-center rounded-4px border-none bg-#1180FF px-22px py-8px text-16px text-white font-400 disabled:cursor-not-allowed disabled:bg-#80C0FF" :disabled="loading">
            <span class="i-svg-spinners-180-ring mr-4px" v-if="loading"></span>
            <span>{{ $t('events.actionSearch') }}</span>
          </button>
          <button type="button" @click="resetForm" class="gray-border ml-16px h-40px cursor-pointer rounded-4px border-none bg-white px-22px py-8px text-16px color-hex-2d2d2d font-400 disabled:cursor-not-allowed" :disabled="loading">
            {{ $t('events.actionReset') }}
          </button>
        </div>
      </div>

      <div class="min-h-900px">
        <template v-if="listing?.length > 0">
          <div class="mt-48px flex flex-wrap" v-for="eventItem in listing" :key="eventItem.date">
            <div class="month-title h-306px w-432px bg-cover bg-no-repeat <2xl:w-384px">
              <h2 class="m-0 mt-8px w-40px pl-182px text-16px color-hex-0078CE font-600 leading-22px <2xl:pl-164px">{{ dayjs(eventItem.date).locale(currentDayjsLocale).format('YYYY') }}</h2>
              <h3 class="m-0 pr-30px text-center text-30px color-hex-FFFFFF font-600 leading-42px tracking-6px">{{ dayjs(eventItem.date).locale(currentDayjsLocale).format('MMMM') }}</h3>
              <div class="m-0 box-border h-234px w-100% px-52px py-32px <2xl:p-32px">
                <p class="m-0 text-24px color-hex-0078CE font-400 leading-24px">{{ $t('events.info1') }}</p>
                <div class="m-0 text-16px color-hex-0078CE font-400 leading-36px">
                  <i18n-t keypath="events.info2" scope="global" tag="div" class="m-0 h-36px truncate">
                    <span class="mx-8px text-28px font-600 leading-36px">{{ eventItem.count }}</span>
                  </i18n-t>
                  <p class="m-0 truncate">{{ $t('events.info3') }}</p>
                  <i18n-t keypath="events.info4" scope="global" tag="div" class="line-clamp-2 m-0 h-72px" v-if="eventItem.city.length">
                    <span class="mx-8px text-28px font-600 leading-36px">{{ eventItem.city.join(' ') }}</span>
                  </i18n-t>
                </div>
              </div>
            </div>

            <div class="events-card ml-32px box-border h-306px w-432px rounded-4px p-24px <2xl:ml-24px <2xl:w-384px" v-for="item in eventItem.list" :key="item.id">
              <h2 class="line-clamp-2 m-0 mb-8px box-border h-60px w-full text-20px color-hex-2D2D2D font-500 leading-30px">
                <template v-if="item.url">
                  <a :href="item.url" class="hover-blue color-hex-2D2D2D font-500 no-underline hover:text-brand-primary" target="_blank">{{ item.title }}</a>
                </template>
                <template v-else>
                  {{ item.title }}
                </template>
              </h2>
              <div class="paragraph line-clamp-4 h-96px text-16px color-hex-9A9EA6 leading-24px" :title="item.info">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div v-html="marked.parse(item.info)"></div>
              </div>
              <span class="time-card my-8px inline-block h-30px h-30px rounded-4px px-16px text-16px color-hex-1180FF font-medium leading-30px">{{ formatDateRange(item?.start ?? '', item?.end ?? '') }}</span>
              <div class="line-clamp-2 box-border h-48px w-full text-16px color-hex-084386 font-400 leading-24px">
                <span class="i-home-icons:position mr-4px mt--4px inline-block align-middle"></span>
                <span>
                  <template v-if="dayjs(item.updateTime).isBefore(dayjs(BREAK_DATE))">
                    {{ item.place }}
                  </template>
                  <template v-else>
                    {{ item.type == 1 ? item.city : $t('events.regionOnline') }}{{ (item.city || item.type != 1) && item.place ? '·' : '' }}{{ item.place }}
                  </template>
                </span>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="mt-48px text-center">{{ $t('events.notFound') }}</div>
        </template>
      </div>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.page-events {
  .events-title {
    background-image: url(@current/assets/project/banner.jpg);
    background-position: right;
  }

  .events-card {
    background: #fff;
    border: 1px solid rgba(154, 158, 166, 0.1);
    box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.04);

    &:nth-child(3n+1) {
      @apply ml-0;
    }

    &:nth-child(n+4) {
      @apply mt-24px;
    }

    .time-card {
      background: rgba(17, 128, 255, 0.1);
    }

    .paragraph {
      &:deep() {
        p {
          @apply my-0 ;
        }

        a {
          color: #1180ff;
          text-decoration: none;
          list-style: none;
        }
      }
    }
  }

  .month-title {
    background: url("https://res.oafimg.cn/-/05896f9b43d234fd/activity-bg2.png");
    background-size: 432px 306px;
  }

  @screen lt-2xl {
    .month-title {
      background: url("https://res.oafimg.cn/-/e9e47ddd6df65e2f/activity-bg1.png");
      background-size: 384px 306px;
    }
  }

  .gray-border {
    border: 1px solid rgba(154, 158, 166, 0.2);
    border-radius: 4px;
    outline-style: none;
  }
}
</style>
